import { generate3DModelFilename } from '@/utils/file-utils';
import { Message } from '@shared/types';
import {
  stlBlobTo3mf,
  type PrintSettings,
} from '@/utils/threeMfExporter';

interface DownloadOptions {
  content: Blob | string;
  filename: string;
  mimeType?: string;
}

interface GenerateDownloadFilenameOptions {
  currentMessage?: Message | null;
  fallback?: string;
  extension: string;
}

/**
 * Downloads a file by creating a temporary download link
 */
export function downloadFile({
  content,
  filename,
  mimeType = 'application/octet-stream',
}: DownloadOptions): void {
  let blob: Blob;

  if (typeof content === 'string') {
    blob = new Blob([content], { type: mimeType });
  } else {
    blob = content;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Generates a filename for downloads using the 3D model filename utility
 */
export function generateDownloadFilename({
  currentMessage,
  fallback = 'parametric-model',
  extension,
}: GenerateDownloadFilenameOptions): string {
  const baseName = generate3DModelFilename({
    conversationTitle: undefined,
    assistantMessage: currentMessage || undefined,
    modelName: undefined,
    fallback,
  });

  return `${baseName}.${extension}`;
}

/**
 * Downloads STL file from blob
 */
export function downloadSTLFile(
  output: Blob,
  currentMessage?: Message | null,
): void {
  const filename = generateDownloadFilename({
    currentMessage,
    extension: 'stl',
  });

  downloadFile({
    content: output,
    filename,
    mimeType: 'application/octet-stream',
  });
}

/**
 * Downloads OpenSCAD code as .scad file
 */
export function downloadOpenSCADFile(
  code: string,
  currentMessage?: Message | null,
): void {
  const filename = generateDownloadFilename({
    currentMessage,
    extension: 'scad',
  });

  downloadFile({
    content: code,
    filename,
    mimeType: 'text/plain',
  });
}

/**
 * Converts the OpenSCAD STL output to a .3MF file and triggers download.
 * The print settings (scale, unit, quality, etc.) are embedded in the 3MF XML
 * so the slicer picks them up automatically.
 */
export async function download3MFFromStlBlob(
  stlBlob: Blob,
  settings: PrintSettings,
  currentMessage?: Message | null,
): Promise<void> {
  const baseName = generateDownloadFilename({
    currentMessage,
    extension: '3mf',
    fallback: settings.modelName || 'parametric-model',
  });

  const threeMfBlob = await stlBlobTo3mf(stlBlob, settings);

  downloadFile({
    content: threeMfBlob,
    filename: baseName,
    mimeType:
      'application/vnd.ms-package.3dmanufacturing-3dmodel+xml',
  });
}
